<script setup>
import {reactive, ref} from 'vue'
import {State, getModels, go} from '@/paks/vu-app'
import Json5 from 'json5'
import {uploadAsset, uploadZip} from '@/paks/vu-dash'
import Zip from 'jszip'

//  TODO
const MaxZip = 5 * 1024 * 1024

const props = defineProps({})

const emit = defineEmits(['input'])

const page = reactive({
    files: null,
    import: null,
    importing: false,
    showImport: false,
    upload: false,
})

//  Component refs
const self = ref(null)
const confirm = ref(null)

async function save(validate) {
    let {files} = page

    page.saving = true
    if (files) {
        const {Manager} = getModels()
        let zip = new Zip()
        let list = await zip.loadAsync(files)
        let manager = await Manager.get({id: State.app.manager.id})
        await uploadZip(manager, list)
    }
    page.saving = false
    validate.clear()
    go({name: 'pages'})
}

function cancel() {
    go({name: 'pages'})
}

/*
    Export the the apps display.json5
 */
async function exportApp() {
    let fullDisplay = State.app.fullDisplay
    let data = Json5.stringify(fullDisplay, null, 4)
    let blob = new Blob([data], {type: 'text/plain'})
    let link = document.createElement('a')
    link.download = 'display.json5'
    if (window.webkitURL != null) {
        link.href = window.webkitURL.createObjectURL(blob)
    } else {
        link.href = window.URL.createObjectURL(blob)
        link.style.display = 'none'
        document.body.appendChild(link)
    }
    link.click()
    emit('input')
}

function showImport() {
    page.showImport = true
    page.importing = false
}

async function importDisplay() {
    let {Manager} = getModels()
    
    page.saving = true
    page.importing = true
    if (!page.import) return null

    try {
        const text = await page.import.text()
        const display = Json5.parse(text)
        let manager = await Manager.get({id: State.app.manager.id})
        await uploadAsset(manager, 'theme/display.js', display)
        State.app.setFullDisplay(display)
    } catch (err) {
        throw new Error(`${page.import.name} contains invalid JSON`)
    } finally {
        page.saving = false
        page.importing = false
        page.showImport = false
    }
}
</script>

<template>
    <div class="view page app-assets">
        <vu-form class="page-form mt-5" help="/doc/ui/view/app-assets.html" ref="self" :data="page" :save="save">
            <div class="vrow">
                <vu-sign name="designer-assets" title="Upload App Assets" color="accent">
                    <p>
                        App assets, such as logos, icons, stylesheets and more, can be used to fully customize the app.
                        The assets are copied to the App's cloud CDN and can update existing app files. Assets under the
                        directory
                        <b>./theme</b>
                        can be preserved when the app is updated if the preserve assets option is checked in the
                        Builder.
                    </p>
                </vu-sign>
                <vu-input v-model="page.upload" label="Upload App Assets" type="checkbox" cols="3" hide-details />
            </div>
            <vu-input
                v-if="page.upload"
                v-model="page.files"
                accept="text/zip"
                class="file-input mb-4"
                density="compact"
                hide-details="auto"
                label="App Assets"
                placeholder="ZIP file"
                name="zip"
                show-size
                type="file"
                :clearable="true"
                :multiple="false" />

            <div class="actions">
                <v-btn size="small" color="accent" type="submit" :loading="page.saving">Save</v-btn>
                <v-btn size="small" color="error" @click="showImport">Import App</v-btn>
                <v-btn size="small" color="teal" @click="exportApp">Export App</v-btn>
                <v-btn size="small" color="none" @click="cancel">Cancel</v-btn>
            </div>
            <vu-confirm ref="confirm" />
        </vu-form>

        <v-dialog v-model="page.showImport" width="550" content-class="import-dialog">
            <v-card class="pb-4">
                <v-card-title class="headline" primary-title>Import Display</v-card-title>
                <v-card-text class="mt-4 px-4"><label>Upload your display.json5 file</label></v-card-text>
                <v-card-text class="px-4">
                    <vu-input
                        v-model="page.import"
                        class="file-input mb-4"
                        density="compact"
                        hide-details="auto"
                        label="Display File"
                        placeholder="display.json5"
                        name="import"
                        show-size
                        type="file"
                        :clearable="true"
                        :multiple="false" />
                </v-card-text>
                <v-card-actions class="px-4">
                    <v-btn
                        :loading="page.importing"
                        ripple
                        color="accent"
                        type="submit"
                        variant="elevated"
                        @click="importDisplay">
                        Import
                    </v-btn>
                    <v-btn ripple variant="elevated" @click="cancel">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<style lang="scss">
.app-assets {
    margin-bottom: 40px;
    .vu-sign {
        width: 100%;
    }
    .v-select-list {
        padding: 0;
        border: solid 1px rgb(var(--v-theme-border-lighten-1));
        border-radius: 0;
    }
    h3 {
        font-weight: normal;
        color: rgb(var(--v-theme-text));
    }
    .page-form {
        max-width: 100% !important;
    }
    .live-data {
        //  When grid, helps alignment
        flex-grow: 30;
    }
}
.import-dialog {
    .headline {
        background: rgb(var(--v-theme-primary));
        color: white;
        font-weight: bold;
        padding: 16px;
    }
}
</style>
